import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { CaretDown, Buildings, SignOut, User, DotsThree, Copy, Trash, Wrench, CaretRight, Eye, PencilSimple } from "phosphor-react";
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Home05Icon, ChartIcon, Folder03Icon, Settings03Icon,
  SourceCodeIcon, WebProgrammingIcon, TableIcon, ConnectIcon,
  Mail01Icon, Add01Icon, MessageEdit01Icon, Store01Icon, LockKeyIcon,
} from '@hugeicons/core-free-icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/icons/IconRegistry";
import { NavLink } from "@/components/NavLink";
import { UsageMeter } from "@/components/UsageMeter";
import { useLocation } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import { useDialogs } from "@/components/dialogs/DialogsProvider";
import { toast } from "sonner";
import { Suspense, lazy } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTheme } from "next-themes";
import { useWorkspaceLoad } from "@/hooks/useWorkspaceLoad";
import { InstalledAppsSidebar } from "@/components/apps/InstalledAppsSidebar";
import { useInstalledApps } from "@/hooks/useInstalledApps";
import type { InstalledApp } from "@/hooks/useInstalledApps";
import { useLaunchSession } from "@/hooks/useLaunchSession";

// Hugeicons wrapper for sidebar — renders with correct size via className
function HIcon({ icon, className = '' }: { icon: any; className?: string }) {
  const sizeMatch = className.match(/h-(\d+(?:\.\d+)?)/);
  const size = sizeMatch ? parseFloat(sizeMatch[1]) * 4 : 18;
  return <HugeiconsIcon icon={icon} size={size} className={className} color="currentColor" strokeWidth={1.5} />;
}

// ============================================================================
// NAVIGATION GROUPS - Phase 1: Sidebar Reorganization
// ============================================================================
// TERMINOLOGY CLARIFICATION:
// - "plugins" in code/routes = "apps" conceptually (e.g., /plugins/database is a data app)
// - "integrations" in code/routes = "connectors" conceptually (OAuth integrations)
// - Routes/API endpoints use "plugins" and "integrations" for backend compatibility
// - UI labels use "Apps" and "Connectors" for better user understanding
// - Comments use "apps" and "connectors" for developer clarity
// - DO NOT rename routes or API endpoints to avoid breaking backend
// ============================================================================
// Structure: 4 groups instead of 3 for better organization
// ============================================================================

// Inline error boundary to prevent sidebar crashes from killing the whole app
class ErrorBoundaryInline extends React.Component<{children: React.ReactNode}, {hasError: boolean}> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) { console.error('[Sidebar ErrorBoundary]', error.message); }
  render() { return this.state.hasError ? null : this.props.children; }
}

// Frame builder stubs — feature discontinued, kept to avoid breaking remaining code
const getIconByName = (_name: string) => null;
// Dialogs removed
const RenameDialog = (_p: any) => null;
const SidePeekPanel = (_p: any) => null;
const ConfirmDialog = (_p: any) => null;
// BroadcastFrameSync stub
const useBroadcastFrameSync = (_cb: any) => ({ broadcast: (_msg: any) => {} });
// Frame type stub
type Frame = { id: string; title: string; iconName?: string; iconColor?: string; customImageUrl?: string; parentId?: string | null; isPublished?: boolean; publishedSlug?: string };
// framesApi stub — frame builder discontinued, functions are no-ops
const framesApi = {
  getFrames: async (): Promise<Frame[]> => [],
  createFrame: async (_data: any): Promise<Frame> => ({ id: '', title: '' }),
  updateFrame: async (_id: string, _data: any): Promise<Frame> => ({ id: '', title: '' }),
  deleteFrame: async (_id: string): Promise<void> => {},
};

// Shared sidebar menu button styles (module scope so all nav components can reference)
const menuButtonClass = `
  text-sm h-9 font-normal rounded-lg transition-all duration-150
  px-3 py-2
  flex items-center
  bg-sidebar-background text-sidebar-foreground
  hover:bg-sidebar-accent hover:text-muted-foreground
  data-[state=active]:bg-sidebar-accent data-[state=active]:text-sidebar-primary
`.replace(/\s+/g, ' ').trim();

// Core Navigation — grouped into categories (General / Compute / Data / Comms).
// Email is a single page (inbox discontinued → send-only), no dropdown.
const navGroups: Array<{ label: string; items: Array<{ id: string; title: string; url: string; icon: any; exact?: boolean }> }> = [
  { label: "General", items: [
    { id: "home",        title: "Home",        url: "/",            icon: Home05Icon, exact: true },
    { id: "analytics",   title: "Analytics",   url: "/analytics",   icon: ChartIcon },
  ]},
  { label: "Compute", items: [
    { id: "functions",   title: "Functions",   url: "/functions",   icon: SourceCodeIcon },
    { id: "hosting",     title: "Hosting",     url: "/hosting",     icon: ConnectIcon },
    { id: "pages",       title: "Pages",       url: "/pages",       icon: WebProgrammingIcon },
  ]},
  { label: "Data", items: [
    { id: "database",    title: "Database",    url: "/database",    icon: TableIcon },
    { id: "files",       title: "Storage",     url: "/files",       icon: Folder03Icon },
    { id: "secrets",     title: "Secrets",     url: "/secrets",     icon: LockKeyIcon },
  ]},
  { label: "Comms", items: [
    { id: "email",       title: "Email",       url: "/email",       icon: Mail01Icon },
  ]},
];

// Flat list kept for backward compatibility with other components.
const mainItems = navGroups.flatMap(g => g.items);
const pluginItems = [...mainItems];



// Workspace Avatar Component — Lumina glass styling
function WorkspaceAvatar({ isExpanded }: { isExpanded: boolean }) {
  const avatarUrl = localStorage.getItem("workspace:avatar");

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt="Workspace"
        className={`${isExpanded ? "h-6 w-6" : "h-5 w-5"} rounded-lg object-cover flex-shrink-0 ring-1 ring-white/15 shadow-sm`}
      />
    );
  }

  return (
    <div className={`${isExpanded ? "h-6 w-6" : "h-5 w-5"} rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center text-accent/80 flex-shrink-0 ring-1 ring-accent/15`}>
      <Buildings className="h-3 w-3" strokeWidth={1.5} />
    </div>
  );
}

// Profile Avatar Component
// Computes MD5 gravatar hash from email if no avatar_url is stored.
// Pure client-side — no extra API call needed.
function md5Gravatar(email: string): string {
  // Use a deterministic hash via encodeURIComponent for a URL-safe identifier
  // Real MD5 not needed — gravatar also supports email directly via SHA256
  // We store gravatar_url in localStorage when profile is fetched
  const hash = email.trim().toLowerCase();
  return `https://www.gravatar.com/avatar/${encodeURIComponent(hash)}?s=200&d=identicon`;
}

function ProfileAvatarIcon({ isExpanded }: { isExpanded: boolean }) {
  const userStr = localStorage.getItem("genofx:user");
  const user = userStr ? JSON.parse(userStr) : null;

  // Priority: stored avatar_url (may be gravatar) → profile:photo → gravatar from email → placeholder
  const avatarUrl =
    user?.avatar_url ||
    user?.gravatar_url ||
    localStorage.getItem("profile:photo") ||
    localStorage.getItem("profile:avatar");

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt="Profile"
        className={`${isExpanded ? "h-6 w-6" : "h-5 w-5"} rounded-full object-cover flex-shrink-0`}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }

  return (
    <div className={`${isExpanded ? "h-6 w-6" : "h-5 w-5"} rounded-full bg-neutral-100 dark:bg-sidebar-accent/50 flex items-center justify-center text-neutral-500 dark:text-sidebar-foreground flex-shrink-0`}>
      <User className="h-3 w-3" strokeWidth={1.5} />
    </div>
  );
}


// Wrapper component for InstalledAppsSidebar that handles hook usage
function InstalledAppsSidebarWrapper({ isCollapsed }: { isCollapsed?: boolean }) {
  const { softwares, webapps, refetch } = useInstalledApps();
  const { activeSessions } = useLaunchSession();
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const handleLaunch = useCallback((app: InstalledApp) => {
    // Always navigate to app detail page — user can see status (Open/Stop/Launch) there
    // This lets them view logs, change env vars, or manage the session
    const route = app.app_type === 'software' ? `/softwares/${app.app_slug}` : `/webapps/${app.app_slug}`;
    navigate(route);
  }, [navigate]);

  const handleUninstall = useCallback(async (app: InstalledApp) => {
    try {
      const res = await authFetch("/api/installed-apps/uninstall", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: app.app_slug }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Uninstall failed");
      }
      toast.success(`${app.name} uninstalled`);
      refetch();
    } catch (err: any) {
      toast.error(err.message || "Failed to uninstall app");
    }
  }, [authFetch, refetch]);

  // Derive active session slugs for green dot indicators
  const activeSessionSlugs = useMemo(
    () => activeSessions
      .filter((s) => s.status === 'running' || s.status === 'launching')
      .map((s) => s.softwareSlug),
    [activeSessions]
  );

  return (
    <InstalledAppsSidebar
      softwares={softwares}
      webapps={webapps}
      onLaunch={handleLaunch}
      onUninstall={handleUninstall}
      activeSessions={activeSessionSlugs}
      isCollapsed={isCollapsed}
    />
  );
}


// Inline Email nav item — collapsible within General menu
function EmailInboxesNav() {
  const [open, setOpen] = useState(false);
  const [inboxes, setInboxes] = useState<{ id: string; address: string; unread_count: number }[]>([]);
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname.startsWith('/email');

  useEffect(() => {
    const token = localStorage.getItem("genofx:token");
    if (!token) return;
    authFetch('/api/email/inboxes')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.inboxes) setInboxes(data.inboxes); })
      .catch(() => {});
  }, [authFetch]);

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={() => setOpen(prev => !prev)}
          isActive={isActive}
          className="text-sm h-9 font-normal rounded-lg transition-all duration-150 px-3 py-2 flex items-center gap-2.5 bg-sidebar-background text-sidebar-foreground hover:bg-sidebar-accent hover:text-muted-foreground data-[state=active]:bg-sidebar-accent data-[state=active]:text-sidebar-primary"
        >
          <HIcon icon={Mail01Icon} className="h-4 w-4 opacity-80 flex-shrink-0" />
          <span className="truncate text-[13px] flex-1">Email</span>
          {open ? <CaretDown className="h-3 w-3 flex-shrink-0" /> : <CaretRight className="h-3 w-3 flex-shrink-0" />}
        </SidebarMenuButton>
      </SidebarMenuItem>
      {open && (
        <>
          {inboxes.length === 0 ? (
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => navigate('/email')} className="text-muted-foreground text-xs gap-2 h-8 px-3 ml-3">
                <span className="truncate">Set up email</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : (
            inboxes.map(inbox => (
              <SidebarMenuItem key={inbox.id}>
                <SidebarMenuButton
                  onClick={() => navigate(`/email?inbox=${inbox.id}`)}
                  className="flex items-center gap-2 h-8 px-3 rounded-lg text-[12px] hover:bg-sidebar-accent ml-3"
                >
                  <span className="truncate flex-1 opacity-80">{inbox.address.split('@')[0]}</span>
                  {inbox.unread_count > 0 && (
                    <span className="text-[9px] font-semibold bg-accent/20 text-accent px-1 py-0 rounded-full">
                      {inbox.unread_count > 99 ? '99+' : inbox.unread_count}
                    </span>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))
          )}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => navigate('/email')} className="flex items-center gap-2 h-8 px-3 rounded-lg text-[12px] hover:bg-sidebar-accent ml-3 text-muted-foreground/60">
              <HIcon icon={Add01Icon} className="h-3 w-3 flex-shrink-0" />
              <span>New inbox</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </>
      )}
    </>
  );
}

// AI Builder Conversations sidebar section
function AIConversationsNav() {
  const [open, setOpen] = useState(true);
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const authFetch = useAuthFetch();
  const { confirm } = useDialogs();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("genofx:token");
    if (!token) { setLoading(false); return; }
    authFetch('/api/ai/projects')
      .then(r => r.ok ? r.json() : { projects: [] })
      .then(data => { setConversations(data.projects || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authFetch]);

  return (
    <SidebarGroup className="py-0">
      <SidebarGroupLabel
        className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50 px-3 h-7 select-none cursor-pointer"
        onClick={() => setOpen(prev => !prev)}
      >
        {open ? <CaretDown className="h-3 w-3 flex-shrink-0 mr-1" /> : <CaretRight className="h-3 w-3 flex-shrink-0 mr-1" />}
        Projects
      </SidebarGroupLabel>

      {open && (
        <SidebarGroupContent>
          <SidebarMenu className="gap-0.5">
            {loading ? (
              <>
                <SidebarMenuItem><div className="h-8 mx-2 rounded bg-muted/30 animate-pulse" /></SidebarMenuItem>
                <SidebarMenuItem><div className="h-8 mx-2 rounded bg-muted/30 animate-pulse" /></SidebarMenuItem>
                <SidebarMenuItem><div className="h-8 mx-2 rounded bg-muted/30 animate-pulse" /></SidebarMenuItem>
              </>
            ) : conversations.length === 0 ? (
              <SidebarMenuItem>
                <SidebarMenuButton onClick={() => navigate('/builder')} className="text-muted-foreground text-xs gap-2 h-8 px-3">
                  <span>Start a conversation</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : (
              conversations.map((c: any) => (
                <SidebarMenuItem key={c.id} className="group/project">
                  <div className={`${menuButtonClass} grid grid-cols-[1fr_auto] gap-1 items-center pr-1`}>
                    <SidebarMenuButton
                      onClick={() => navigate(`/builder/${c.id}`)}
                      className="flex items-center gap-2 h-8 px-3 rounded-lg text-[12px] hover:bg-sidebar-accent"
                      title={c.name || 'Untitled'}
                    >
                      <span className="truncate max-w-[140px] opacity-90">{c.name || 'Untitled'}</span>
                    </SidebarMenuButton>
                    {/* Three-dot menu for projects */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 hover:bg-white/10 rounded opacity-0 group-hover/project:opacity-100 transition-opacity"
                        >
                          <DotsThree className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => navigate(`/builder/${c.id}`)}>
                          <Eye className="h-3.5 w-3.5 mr-2" /> Open
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {/* TODO: rename via API */}}>
                          <PencilSimple className="h-3.5 w-3.5 mr-2" /> Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={async () => {
                            if (!(await confirm({ title: `Delete "${c.name || 'Untitled'}"?`, description: 'This cannot be undone.', confirmText: 'Delete', variant: 'danger' }))) return;
                            try {
                              const token = localStorage.getItem('genofx:token');
                              const base = import.meta.env.VITE_API_BASE || 'https://api.genofx.com';
                              const r = await fetch(`${base}/api/ai/projects/${c.id}`, {
                                method: 'DELETE',
                                headers: token ? { Authorization: `Bearer ${token}` } : {},
                              });
                              if (r.ok) {
                                toast.success('Conversation deleted');
                                setConversations(prev => prev.filter(x => x.id !== c.id));
                              } else toast.error('Delete failed');
                            } catch { toast.error('Delete failed'); }
                          }}
                        >
                          <Trash className="h-3.5 w-3.5 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </SidebarMenuItem>
              ))
            )}
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => navigate('/builder')} className="flex items-center gap-2 h-8 px-3 rounded-lg text-[12px] hover:bg-sidebar-accent text-muted-foreground/60">
                <HIcon icon={Add01Icon} className="h-3 w-3 flex-shrink-0" />
                <span>New conversation</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  );
}

export function AppSidebar({ initialLoading = false }: { initialLoading?: boolean }) {
  const { open, isMobile, openMobile, setOpenMobile } = useSidebar();
  const isExpanded = isMobile ? openMobile : open;
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const authFetch = useAuthFetch();

  const userStr = localStorage.getItem("genofx:user");
  const currentUser = useMemo(() => userStr ? JSON.parse(userStr) : null, [userStr]);

  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(localStorage.getItem("genofx:current_workspace_id"));

  // App Reorder removed in v3 — Cosmos do not need reordering

  const fetchWorkspaces = useCallback(async () => {
    // Don't attempt API call without auth token — avoids 401 on unauthenticated pages
    const token = localStorage.getItem("genofx:token");
    if (!token) {
      setIsLoadingWorkspaces(false);
      return;
    }

    try {
      const response = await authFetch("/api/workspaces");

      if (response.ok) {
        const data = await response.json();
        setWorkspaces(data.workspaces || []);

        if (data.workspaces?.length > 0 && !currentWorkspaceId) {
          const firstId = String(data.workspaces[0].id);
          setCurrentWorkspaceId(firstId);
          localStorage.setItem("genofx:current_workspace_id", firstId);
          if (data.workspaces[0].avatar_url) {
            localStorage.setItem("workspace:avatar", data.workspaces[0].avatar_url);
          }
        }
      }
    } catch (error) {
      console.error("[AppSidebar] Error fetching workspaces:", error);
    } finally {
      setIsLoadingWorkspaces(false);
    }
  }, [authFetch, currentWorkspaceId]);

  useEffect(() => {
    fetchWorkspaces();
    window.addEventListener("GENOFX_WORKSPACES_UPDATED", fetchWorkspaces);
    return () => window.removeEventListener("GENOFX_WORKSPACES_UPDATED", fetchWorkspaces);
  }, [fetchWorkspaces]);

  const activeWorkspace = workspaces.find(w => String(w.id) === String(currentWorkspaceId)) || workspaces[0];

  const handleWorkspaceChange = (id: string) => {
    const ws = workspaces.find(w => String(w.id) === String(id));
    if (ws) {
      // Hard refresh for workspace switch
      setCurrentWorkspaceId(String(id));
      localStorage.setItem("genofx:current_workspace_id", String(id));

      if (ws.avatar_url) {
        localStorage.setItem("workspace:avatar", ws.avatar_url);
      } else {
        localStorage.removeItem("workspace:avatar");
      }

      // Force reload to clear all state/hooks
      window.location.reload();
    }
  };

  const confirmSignOut = async () => {
    // Call server-side logout to invalidate tokens + revoke RDS sessions
    try {
      const apiBase = import.meta.env.VITE_API_BASE || 'https://api.genofx.com';
      await fetch(`${apiBase}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('genofx:token') || ''}`,
          'x-access-token': localStorage.getItem('genofx:accessToken') || '',
          'x-session-id': localStorage.getItem('genofx:sessionId') || '',
        },
      });
    } catch (err) {
      // Server logout failed — still clear local state
      console.warn('[Logout] Server-side logout failed:', err);
    }

    // Clear ALL auth-related localStorage
    const keysToRemove = [
      'genofx:token', 'genofx:accessToken', 'genofx:refreshToken',
      'genofx:username', 'genofx:user', 'genofx:email', 'genofx:sessionId',
      'genofx:current_workspace_id', 'workspace:avatar',
      'profile:name', 'profile:username', 'profile:photo',
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
    navigate('/auth/login');
  };


  const [accountOpen, setAccountOpen] = useState(false);
  const { load: workspaceLoad, aiCredits } = useWorkspaceLoad();

  // User Pages State (stored as frames in the backend for compatibility)
  const [userFrames, setUserFrames] = useState<Frame[]>([]);
  const [framesLoading, setFramesLoading] = useState(true);

  const framesFetchRef = useRef<{ inFlight: boolean; lastWorkspaceId: string | null; lastFetchedAt: number }>({
    inFlight: false,
    lastWorkspaceId: null,
    lastFetchedAt: 0
  });

  // Multi-tab sync using BroadcastChannel
  const { broadcast } = useBroadcastFrameSync((message) => {
    console.log('[AppSidebar] Received broadcast from another tab:', message);
    // Handle different message types for cross-tab sync
    switch (message.type) {
      case 'FRAME_CREATED':
        if (message.data?.frame) {
          setUserFrames(prev => [...prev, message.data.frame]);
        }
        break;
      case 'FRAME_UPDATED':
        if (message.frameId && message.data?.updates) {
          setUserFrames(prev =>
            prev.map(frame =>
              frame.id === message.frameId
                ? { ...frame, ...message.data.updates }
                : frame
            )
          );
        }
        break;
      case 'FRAME_DELETED':
        if (message.frameId) {
          setUserFrames(prev => prev.filter(f => f.id !== message.frameId));
        }
        break;
      case 'FRAMES_RELOADED':
        // Another tab reloaded frames, refresh ours
        loadUserFrames();
        break;
    }
  });

  // Dialog States
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; frameId?: string; title?: string }>({ open: false, frameId: '', title: '' });
  const [sidePeek, setSidePeek] = useState<{ open: boolean; frameId?: string; title?: string }>({ open: false, frameId: '', title: '' });
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; frameId?: string; title?: string }>({ open: false, frameId: '', title: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [collapsedFrames, setCollapsedFrames] = useState<Set<string>>(new Set());
  const [inlineCreating, setInlineCreating] = useState<{ parentId: string | null; editingId?: string } | null>(null);
  const [inlineTitle, setInlineTitle] = useState('');
  const [publishedFrames, setPublishedFrames] = useState<Set<string>>(new Set());
  const [publicUrlDialog, setPublicUrlDialog] = useState<{ open: boolean; frameId?: string; url?: string }>({ open: false });

  const isActive = (path: string, exact = false) => {
    // Handle URLs with query params (e.g. /discover?tab=community)
    const qIdx = path.indexOf('?');
    if (qIdx !== -1) {
      const basePath = path.substring(0, qIdx);
      const queryPart = path.substring(qIdx);
      return currentPath === basePath && location.search === queryPart;
    }
    if (exact) return currentPath === path;
    return currentPath.startsWith(path);
  };

  const handleCreateSubpage = useCallback(async (parentId: string) => {
    try {
      const newFrame = await framesApi.createFrame({
        title: 'Untitled Page',
        parentId
      });
      const event = new CustomEvent('GENOFX_FRAME_CREATED', { detail: { frame: newFrame } });
      window.dispatchEvent(event);
      navigate(`/page/${newFrame.id}`);
    } catch (error) {
      console.error('[AppSidebar] Failed to create subpage:', error);
      toast.error('Failed to create subpage');
    }
  }, [navigate]);

  const handleInlineCreate = useCallback((parentId: string | null) => {
    setInlineCreating({ parentId });
    setInlineTitle('');
    // Auto-expand parent if creating subpage
    if (parentId) {
      setCollapsedFrames(prev => {
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
    }
  }, []);

  const handleInlineSubmit = useCallback(async () => {
    if (!inlineTitle.trim() || !inlineCreating) {
      setInlineCreating(null);
      setInlineTitle('');
      return;
    }

    try {
      const newFrame = await framesApi.createFrame({
        title: inlineTitle.trim(),
        parentId: inlineCreating.parentId || undefined
      });
      const event = new CustomEvent('GENOFX_FRAME_CREATED', { detail: { frame: newFrame } });
      window.dispatchEvent(event);
      toast.success('Page created');
      setInlineCreating(null);
      setInlineTitle('');
    } catch (error) {
      console.error('[AppSidebar] Failed to create page:', error);
      toast.error('Failed to create page');
    }
  }, [inlineTitle, inlineCreating]);

  const handleInlineCancel = useCallback(() => {
    setInlineCreating(null);
    setInlineTitle('');
  }, []);

  const handlePublishToggle = useCallback(async (frameId: string) => {
    if (!currentWorkspaceId) return;
    try {
      const isPublished = publishedFrames.has(frameId);
      const method = isPublished ? 'DELETE' : 'POST';
      const response = await authFetch(`/api/ws/${currentWorkspaceId}/frames/${frameId}/publish`, { method });

      if (response.ok) {
        const data = await response.json();
        setPublishedFrames(prev => {
          const next = new Set(prev);
          if (isPublished) {
            next.delete(frameId);
          } else {
            next.add(frameId);
          }
          return next;
        });

        if (!isPublished) {
          setPublicUrlDialog({ open: true, frameId, url: data.publicUrl });
        }
        toast.success(isPublished ? 'Page unpublished' : 'Page published');
      } else {
        toast.error('Failed to update publish status');
      }
    } catch (error) {
      console.error('Publish toggle failed:', error);
      toast.error('Failed to update publish status');
    }
  }, [currentWorkspaceId, authFetch, publishedFrames]);

  const toggleCollapse = useCallback((frameId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCollapsedFrames(prev => {
      const next = new Set(prev);
      if (next.has(frameId)) {
        next.delete(frameId);
      } else {
        next.add(frameId);
      }
      return next;
    });
  }, []);

  const renderFrameTree = useCallback((parentId: string | null, depth: number) => {
    const MAX_DEPTH = 5;
    const children = userFrames.filter(frame => (frame.parentId || null) === parentId);

    return children.map((frame) => {
      const FrameIcon = getIconByName(frame.iconName);
      const iconColor = frame.iconColor || undefined;
      const hasCustomImage = !!frame.customImageUrl;
      const hasChildren = userFrames.some(f => f.parentId === frame.id);
      const isCollapsed = collapsedFrames.has(frame.id);
      const canAddSubpage = depth < MAX_DEPTH;
      const isSidebarCollapsed = !isExpanded;
      const isCurrent = isActive(`/page/${frame.id}`) || isActive(`/frame/${frame.id}`);
      const iconSize = isSidebarCollapsed ? 18 : 16;
      const iconBoxSize = isSidebarCollapsed ? "2.25rem" : "1.75rem";

      return (
        <React.Fragment key={frame.id}>
          <SidebarMenuItem className={isSidebarCollapsed ? "flex justify-center" : ""}>
            <div
              className={`group/frame ${menuButtonClass} relative grid ${isSidebarCollapsed ? "grid-cols-1 !w-8 !h-8 !min-w-[32px] !p-0 justify-center flex items-center" : "grid-cols-[1fr_auto]"} gap-1 items-center ${isCurrent ? 'bg-sidebar-border text-sidebar-accent-foreground shadow-sm' : ''} hover:bg-sidebar-accent transition-colors duration-150`}
            >
              <NavLink
                to={`/page/${frame.id}`}
                className={`flex items-center min-w-0 ${isSidebarCollapsed ? "justify-center w-full h-full" : "gap-2"}`}
                style={{ paddingLeft: isSidebarCollapsed ? 0 : depth * 14 + 6 }}
                title={frame.title}
              >
                {/* Collapse/Expand Arrow */}
                {!isSidebarCollapsed && hasChildren && (
                  <button
                    onClick={(e) => toggleCollapse(frame.id, e)}
                    className="shrink-0 -ml-1 p-0.5 hover:bg-neutral-200 dark:hover:bg-white/10 rounded transition-transform"
                    style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
                  >
                    <CaretRight className="h-3.5 w-3.5" />
                  </button>
                )}

                {/* Icon */}
                {hasCustomImage ? (
                  <img src={frame.customImageUrl || ''} alt="" className={`${isSidebarCollapsed ? 'w-5 h-5' : 'w-4 h-4'} rounded-sm object-cover shrink-0`} />
                ) : (
                  <span
                    className="inline-flex items-center justify-center rounded-md shrink-0 transition-transform duration-200 group-hover/frame:scale-110"
                    style={{
                      background: isSidebarCollapsed ? 'transparent' : `${iconColor}15`,
                      borderRadius: '6px',
                      width: isSidebarCollapsed ? '1.5rem' : '1.75rem',
                      height: isSidebarCollapsed ? '1.5rem' : '1.75rem'
                    }}
                  >
                    <FrameIcon size={iconSize} weight="duotone" style={{ color: iconColor }} />
                  </span>
                )}

                {/* Title */}
                {!isSidebarCollapsed && <span className="truncate text-[13px] font-medium opacity-90">{frame.title}</span>}
              </NavLink>

              {/* Action Buttons - Grid second column, always visible */}
              {!isSidebarCollapsed && (
                <div className="flex items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover/frame:opacity-100 transition-opacity flex-shrink-0 relative z-10 pr-2">
                  {/* Add Subpage Button */}
                  {canAddSubpage && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleInlineCreate(frame.id); }}
                      className="p-1 hover:bg-white/10 rounded-sm cursor-pointer text-neutral-400 hover:text-white"
                      title="Add subpage inline"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  )}

                  {/* Three-dot Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} className="p-1 hover:bg-white/10 rounded-sm cursor-pointer text-neutral-400 hover:text-white transition-colors">
                        <DotsThree className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-200">
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); navigate(`/page/${frame.id}`); }} className="focus:bg-neutral-100 dark:focus:bg-white/10 focus:text-neutral-900 dark:focus:text-white cursor-pointer"><Eye className="h-4 w-4 mr-2" /> Open</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-neutral-200 dark:bg-white/5" />
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); handlePublishToggle(frame.id); }} className="focus:bg-neutral-100 dark:focus:bg-white/10 focus:text-neutral-900 dark:focus:text-white cursor-pointer"><Globe className="h-4 w-4 mr-2" /> {publishedFrames.has(frame.id) ? 'Unpublish' : 'Publish'}</DropdownMenuItem>
                      {publishedFrames.has(frame.id) && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            const publicUrl = `${window.location.origin}/public/${currentWorkspaceId}/${frame.id}`;
                            navigator.clipboard.writeText(publicUrl);
                            toast.success('Public URL copied');
                          }}
                          className="focus:bg-neutral-100 dark:focus:bg-white/10 focus:text-neutral-900 dark:focus:text-white cursor-pointer text-xs text-neutral-500 dark:text-neutral-400"
                        >
                          <Copy className="h-4 w-4 mr-2" /> Copy Public Link
                        </DropdownMenuItem>
                      )}
                      {canAddSubpage && (
                        <DropdownMenuItem onClick={(e) => { e.preventDefault(); handleCreateSubpage(frame.id); }} className="focus:bg-neutral-100 dark:focus:bg-white/10 focus:text-neutral-900 dark:focus:text-white cursor-pointer"><Plus className="h-4 w-4 mr-2" /> Add Subpage</DropdownMenuItem>
                      )}
                      {!canAddSubpage && (
                        <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed"><Plus className="h-4 w-4 mr-2" /> Max depth reached</DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); setRenameDialog({ open: true, frameId: frame.id, title: frame.title }); }} className="focus:bg-neutral-100 dark:focus:bg-white/10 focus:text-neutral-900 dark:focus:text-white cursor-pointer"><Wrench className="h-4 w-4 mr-2" /> Rename</DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.preventDefault(); setSidePeek({ open: true, frameId: frame.id, title: frame.title }); }} className="focus:bg-neutral-100 dark:focus:bg-white/10 focus:text-neutral-900 dark:focus:text-white cursor-pointer"><Eye className="h-4 w-4 mr-2" /> Side Peek</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-neutral-200 dark:bg-white/5" />
                      <DropdownMenuItem className="text-red-500 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400 focus:bg-red-50 dark:focus:bg-red-400/10 cursor-pointer" onClick={(e) => { e.preventDefault(); setConfirmDelete({ open: true, frameId: frame.id, title: frame.title }); }}>
                        <Trash className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </SidebarMenuItem>
          {/* Render children only if not collapsed */}
          {hasChildren && !isCollapsed && renderFrameTree(frame.id, depth + 1)}
          {/* Inline creation for subpages */}
          {inlineCreating && inlineCreating.parentId === frame.id && !isCollapsed && (
            <SidebarMenuItem>
              <div className="flex items-center gap-2 px-2 py-1.5" style={{ paddingLeft: (depth + 1) * 16 + 8 }}>
                <Input
                  autoFocus
                  value={inlineTitle}
                  onChange={(e) => setInlineTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleInlineSubmit();
                    if (e.key === 'Escape') handleInlineCancel();
                  }}
                  onBlur={handleInlineCancel}
                  placeholder="Page title..."
                  className="h-8 text-sm"
                />
              </div>
            </SidebarMenuItem>
          )}
        </React.Fragment>
      );
    });
  }, [userFrames, isActive, menuButtonClass, navigate, collapsedFrames, toggleCollapse, inlineCreating, inlineTitle, handleInlineSubmit, handleInlineCancel, handleInlineCreate, publishedFrames, handlePublishToggle, isExpanded]);

  const loadUserFrames = useCallback(async () => {
    const workspaceId = currentWorkspaceId || localStorage.getItem('genofx:current_workspace_id');
    if (!workspaceId) {
      setUserFrames([]);
      setFramesLoading(false);
      return;
    }
    const now = Date.now();
    const sameWorkspace = framesFetchRef.current.lastWorkspaceId === workspaceId;
    const elapsed = now - framesFetchRef.current.lastFetchedAt;

    if (framesFetchRef.current.inFlight) {
      return;
    }
    if (sameWorkspace && elapsed < 2000) {
      return;
    }

    try {
      framesFetchRef.current = {
        inFlight: true,
        lastWorkspaceId: workspaceId,
        lastFetchedAt: now,
      };
      const response = await framesApi.getFrames();
      // Transform backend frames to expected format if needed, but assuming they match Frame type
      setUserFrames(response);
    } catch (error) {
      console.error("Failed to load pages", error);
    } finally {
      framesFetchRef.current = {
        ...framesFetchRef.current,
        inFlight: false,
      };
      setFramesLoading(false);
    }
  }, [currentWorkspaceId]);

  // Handle frame creation - add new frame to sidebar immediately
  const handleFrameCreated = useCallback((event: Event) => {
    const customEvent = event as CustomEvent;
    const newFrame = customEvent.detail?.frame;
    if (newFrame) {
      console.log('[AppSidebar] Frame created:', newFrame.id);
      setUserFrames(prevFrames => [...prevFrames, newFrame]);
      // Broadcast to other tabs
      broadcast({ type: 'FRAME_CREATED', frameId: newFrame.id, data: { frame: newFrame } });
    }
  }, [broadcast]);

  // Handle frame updates - update specific frame in sidebar
  const handleFrameUpdated = useCallback((event: Event) => {
    const customEvent = event as CustomEvent;
    const { frameId, updates } = customEvent.detail || {};
    if (frameId && updates) {
      console.log('[AppSidebar] Frame updated:', frameId, updates);
      setUserFrames(prevFrames =>
        prevFrames.map(frame =>
          frame.id === frameId
            ? { ...frame, ...updates }
            : frame
        )
      );
      // Broadcast to other tabs
      broadcast({ type: 'FRAME_UPDATED', frameId, data: { updates } });
    }
  }, [broadcast]);

  // Handle frame deletion - remove frame from sidebar
  const handleFrameDeleted = useCallback((event: Event) => {
    const customEvent = event as CustomEvent;
    const frameId = customEvent.detail?.frameId;
    if (frameId) {
      console.log('[AppSidebar] Frame deleted:', frameId);
      setUserFrames(prevFrames => prevFrames.filter(frame => frame.id !== frameId));
      // Broadcast to other tabs
      broadcast({ type: 'FRAME_DELETED', frameId });
    }
  }, [broadcast]);

  useEffect(() => {
    // Auth-dependent API calls — only fire when the user has a token
    const token = localStorage.getItem("genofx:token");
    if (token) {
      loadUserFrames();
    }
    
    // Listen for frame events from other components
    window.addEventListener('GENOFX_FRAME_CREATED', handleFrameCreated);
    window.addEventListener('GENOFX_FRAME_UPDATED', handleFrameUpdated);
    window.addEventListener('GENOFX_FRAME_DELETED', handleFrameDeleted);

    return () => {
      window.removeEventListener('GENOFX_FRAME_CREATED', handleFrameCreated);
      window.removeEventListener('GENOFX_FRAME_UPDATED', handleFrameUpdated);
      window.removeEventListener('GENOFX_FRAME_DELETED', handleFrameDeleted);
    };
  }, [currentWorkspaceId, loadUserFrames, handleFrameCreated, handleFrameUpdated, handleFrameDeleted]);

  return (
    <>
      <Sidebar variant="sidebar" collapsible="none" className="z-[100] w-[220px] border-r border-border/50 transition-all duration-300">
        <SidebarHeader className="p-2 border-b border-border/40">
          {!isMobile && (
            <div className="w-full">
              {isExpanded ? (
                <div className="w-full h-auto py-2 px-2.5 flex items-center gap-2.5 justify-start rounded-xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm" aria-label="Account">
                  <img src="/logo-primary.png" alt="GenofX" className="h-8 object-contain" />
                </div>
              ) : (
                <div className="h-10 w-10 mx-auto p-0 flex items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.03]" aria-label="Account">
                  <img src="/genofx-icon.png" alt="GenofX" className="h-7 w-7 object-contain" />
                </div>
              )}
            </div>
          )}
          {isMobile && (
            <div className={`flex items-center gap-3 h-12 transition-all duration-300 ${isExpanded ? "px-4" : "justify-center"}`}>
              <Icon name="home" size="md" className="text-foreground flex-shrink-0" />
              {isExpanded && (
                <span className="font-bold text-base tracking-tight text-foreground select-none">GenofX</span>
              )}
            </div>
          )}
        </SidebarHeader>

        <SidebarContent className="px-1 pt-1">
          <ScrollArea className="flex-1">
            {/* Grouped navigation: General / Compute / Data / Comms */}
            {navGroups.map((group) => (
              <SidebarGroup key={group.label} className="py-0 mt-1">
                <SidebarGroupLabel className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="gap-0.5">
                    {group.items.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive(item.url, item.exact)}
                          tooltip={item.title}
                          className={menuButtonClass}
                        >
                          <NavLink to={item.url} className="flex items-center gap-2.5 w-full">
                            <HIcon icon={item.icon} className="h-4 w-4 opacity-80 flex-shrink-0" />
                            <span className="truncate text-[13px]">{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}

            {/* Workspace: Marketplace + Settings */}
            <SidebarGroup className="py-0 mt-1">
              <SidebarGroupLabel className="px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                Workspace
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-0.5">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={currentPath.startsWith('/marketplace')}
                      className={menuButtonClass}
                    >
                      <NavLink to="/marketplace" className="flex items-center gap-2.5 w-full">
                        <HIcon icon={Store01Icon} className="h-4 w-4 opacity-80 flex-shrink-0" />
                        <span className="truncate text-[13px]">Marketplace</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={currentPath.startsWith('/settings')}
                      className={menuButtonClass}
                    >
                      <NavLink to="/settings" className="flex items-center gap-2.5 w-full">
                        <HIcon icon={Settings03Icon} className="h-4 w-4 opacity-80 flex-shrink-0" />
                        <span className="truncate text-[13px]">Settings</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <div className="h-3" />

            {/* Projects (AI builder projects) */}
            <AIConversationsNav />

            <div className="h-3" />
          </ScrollArea>
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter className={`border-t border-border/40 p-2 transition-all duration-300 ${!isExpanded ? "flex justify-center px-0" : ""}`}>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu open={accountOpen} onOpenChange={setAccountOpen}>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    className={`h-[42px] data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground rounded-xl transition-all duration-300 ${!isExpanded ? "w-8 h-8 p-0 flex items-center justify-center mx-auto" : ""}`}
                    tooltip={!isExpanded ? (currentUser?.full_name || currentUser?.username || "Account") : undefined}
                  >
                    <ProfileAvatarIcon isExpanded={isExpanded} />
                    {isExpanded && (
                      <div className="grid flex-1 text-left text-sm leading-tight ml-2 overflow-hidden">
                        <span className="truncate font-semibold text-foreground tracking-tight">
                          {currentUser?.full_name || currentUser?.username || "Account"}
                        </span>
                        <span className="truncate text-[10px] text-muted-foreground uppercase tracking-widest font-medium opacity-70">
                          {currentUser?.email || "Workspace User"}
                        </span>
                      </div>
                    )}
                    {isExpanded && <CaretDown className="ml-auto size-3 text-muted-foreground opacity-50 group-data-[state=open]:rotate-180 transition-transform" />}
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-64 mb-2 bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-2xl p-1.5 z-[110]"
                  side={isExpanded ? "top" : "right"}
                  align={isExpanded ? "end" : "start"}
                  sideOffset={12}
                >
                  <div className="px-2 py-2 mb-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">My Account</p>
                  </div>

                  {/* Credits + free-tier usage */}
                  <div className="px-1 pb-1"><UsageMeter variant="menu" /></div>

                  <div className="space-y-0.5">
                    <DropdownMenuItem className="gap-2.5 px-2.5 h-9 rounded-xl cursor-pointer focus:bg-sidebar-accent transition-colors" onClick={() => navigate('/settings')}>
                      <HIcon icon={Settings03Icon} className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-2.5 px-2.5 h-9 rounded-xl cursor-pointer focus:bg-sidebar-accent transition-colors" onClick={() => setFeedbackOpen(true)}>
                      <HIcon icon={MessageEdit01Icon} className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Send Feedback</span>
                    </DropdownMenuItem>
                  </div>

                  <DropdownMenuSeparator className="my-1.5 opacity-50" />

                  {/* Workspace Load Meter */}
                  {workspaceLoad && (
                    <>
                      <div className="px-2.5 py-2 space-y-2.5">
                        {/* CU Load */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/60">Load</span>
                            <span className={`text-[10px] font-semibold tabular-nums ${
                              workspaceLoad.status === 'empty' ? 'text-red-500' :
                              workspaceLoad.status === 'warning' ? 'text-amber-500' :
                              'text-muted-foreground'
                            }`}>
                              {workspaceLoad.currentTokens}/{workspaceLoad.capacity} CU
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-muted/50 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                workspaceLoad.status === 'empty' ? 'bg-red-500' :
                                workspaceLoad.status === 'warning' ? 'bg-amber-500' :
                                'bg-primary'
                              }`}
                              style={{ width: `${Math.min(100, 100 - workspaceLoad.percentage)}%` }}
                            />
                          </div>
                          {workspaceLoad?.appBreakdown && Array.isArray(workspaceLoad.appBreakdown) && workspaceLoad.appBreakdown.length > 0 && (
                            <div className="mt-1.5 space-y-0.5">
                              {workspaceLoad.appBreakdown.slice(0, 3).map((app) => (
                                <div key={app.appId} className="flex items-center justify-between text-[9px]">
                                  <span className="text-muted-foreground truncate max-w-[120px]">{app.appId}</span>
                                  <span className="text-muted-foreground/70 tabular-nums">{app.percentage}%</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* AI Credits */}
                        {aiCredits && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/60">AI Credits</span>
                              <span className={`text-[10px] font-semibold tabular-nums ${
                                aiCredits.percentage >= 100 ? 'text-red-500' :
                                aiCredits.percentage >= 80 ? 'text-amber-500' :
                                'text-muted-foreground'
                              }`}>
                                {aiCredits.creditsUsed}/{aiCredits.creditsLimit}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-muted/50 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  aiCredits.percentage >= 100 ? 'bg-red-500' :
                                  aiCredits.percentage >= 80 ? 'bg-amber-500' :
                                  'bg-violet-500'
                                }`}
                                style={{ width: `${Math.min(100, aiCredits.percentage)}%` }}
                              />
                            </div>
                          </div>
                        )}
                        <button
                          className="text-[9px] text-primary/70 hover:text-primary transition-colors w-full text-right"
                          onClick={(e) => { e.stopPropagation(); navigate('/settings/billing'); }}
                        >
                          View details →
                        </button>
                      </div>
                      <DropdownMenuSeparator className="my-1.5 opacity-50" />
                    </>
                  )}

                  <DropdownMenuSeparator className="my-1.5 opacity-50" />

                  <DropdownMenuItem
                    onSelect={() => setIsSignOutDialogOpen(true)}
                    className="gap-2.5 px-2.5 h-9 rounded-xl cursor-pointer text-red-500 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 transition-colors"
                  >
                    <SignOut className="h-4 w-4" />
                    <span className="text-sm font-semibold">Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      {/* Dialogs ... unchanged ... */}
      <RenameDialog
        open={renameDialog.open}
        onClose={() => setRenameDialog({ ...renameDialog, open: false })}
        currentTitle={renameDialog.title || ''}
        onRename={async (newTitle) => {
          if (renameDialog.frameId) {
            try {
              await framesApi.updateFrame(renameDialog.frameId, { title: newTitle });
              window.dispatchEvent(new Event("GENOFX_FRAMES_UPDATED"));
              toast.success("Page renamed");
            } catch (e) {
              toast.error("Failed to rename page");
            }
          }
          setRenameDialog({ ...renameDialog, open: false });
        }}
      />

      <SidePeekPanel
        open={sidePeek.open}
        onClose={() => setSidePeek(prev => ({ ...prev, open: false }))}
        frameId={sidePeek.frameId || ''}
        frameTitle={sidePeek.title || ''}
      />

      <ConfirmDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ ...confirmDelete, open: false })}
        title="Delete Frame"
        description={`Are you sure you want to delete "${confirmDelete.title}"?`}
        variant="danger"
        loading={deleteLoading}
        onConfirm={async () => {
          if (confirmDelete.frameId) {
            setDeleteLoading(true);
            try {
              await framesApi.deleteFrame(confirmDelete.frameId);
              toast.success("Frame deleted");
              // Dispatch specific GENOFX_FRAME_DELETED event
              const event = new CustomEvent('GENOFX_FRAME_DELETED', {
                detail: { frameId: confirmDelete.frameId }
              });
              window.dispatchEvent(event);
              // Also dispatch generic update for backward compatibility
              window.dispatchEvent(new Event("GENOFX_FRAMES_UPDATED"));
              if (currentPath.includes(confirmDelete.frameId)) {
                navigate('/');
              }
            } catch (e) {
              toast.error("Failed to delete frame");
            } finally {
              setDeleteLoading(false);
              setConfirmDelete({ ...confirmDelete, open: false });
            }
          }
        }}
      />

      {/* Sign Out Dialog */}
      <AlertDialog open={isSignOutDialogOpen} onOpenChange={setIsSignOutDialogOpen}>
        <AlertDialogContent className="rounded-[24px] border-border/40 max-w-[400px] p-6 shadow-2xl bg-background/95 z-[9999]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold">Sign out of Genofx?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed pt-2">
              Are you sure you want to sign out? You'll need to enter your credentials again to access your personal account and shared workspaces.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[12px] h-11 px-6 font-semibold border-border/40 hover:bg-muted/50 transition-colors">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSignOut}
              className="rounded-[12px] h-11 px-6 bg-red-500 hover:bg-red-600 text-white border-none font-bold transition-all shadow-md shadow-red-500/20 active:scale-95"
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feedback Dialog */}
      {feedbackOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => setFeedbackOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative z-10 bg-card border border-border/50 rounded-2xl p-5 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <HIcon icon={MessageEdit01Icon} className="h-5 w-5 text-accent" />
              <h3 className="text-base font-bold">Send Feedback</h3>
            </div>
            <textarea
              className="w-full h-28 bg-background/60 border border-border/50 rounded-xl p-3 text-sm resize-none placeholder:text-muted-foreground/50 outline-none focus:border-accent/50 transition-colors"
              placeholder="Share bugs, suggestions, or anything on your mind…"
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button
                className="px-4 py-1.5 rounded-xl text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => { setFeedbackOpen(false); setFeedbackText(''); }}
              >Cancel</button>
              <button
                className="px-4 py-1.5 rounded-xl text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                disabled={!feedbackText.trim() || feedbackSending}
                onClick={async () => {
                  if (!feedbackText.trim()) return;
                  setFeedbackSending(true);
                  try {
                    const token = localStorage.getItem('genofx:token');
                    const base = import.meta.env.VITE_API_BASE ?? 'https://api.genofx.com';
                    await fetch(`${base}/api/feedback`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                      body: JSON.stringify({ text: feedbackText, path: window.location.pathname }),
                    });
                    toast.success('Feedback sent — thank you!');
                    setFeedbackOpen(false);
                    setFeedbackText('');
                  } catch {
                    toast.error('Failed to send feedback');
                  } finally { setFeedbackSending(false); }
                }}
              >{feedbackSending ? 'Sending…' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
